import { ConfirmEmailAddressInterface, RegisterBasicInfoInterface, RegisterInterface, RegisterResponse, SendConfirmPhoneNumberSMSInterface } from "../models/Register";
import { LoginInterface, LoginResponse, ResetPasswordInterface } from "../models/Login";
import { fetchWithAuth, defaultFetch, isLoggedIn, setNormalToken, setViewAsToken, setProfile, setViewAsProfile, updateUserAllocationRights } from "../utils/auth";
import { ConfirmEmailResponse, LoginApiResponse, RegisterApiResponse } from "src/models/types";
import { ConfirmEmailData } from "src/models/registration/confirm-email-data";
import { extractErrorMessageFromProblemDetails } from "src/utils/string-utils";
import { ProblemDetails } from "src/models/ProblemDetails";

export const registerProfile = async (registerData: RegisterInterface): Promise<RegisterApiResponse> => {
  try {
    const body: string = JSON.stringify(registerData);
    const response = await defaultFetch(`/api/backend/authentication/register-profile`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });

    if (response.status === 400) {
      const problemDetails: ProblemDetails = await response.json();
      return new RegisterApiResponse({ error: problemDetails });
    }

    if (response.status >= 500) {
      return new RegisterApiResponse({ error: "Unable to complete registration. Please contact support at audience@thatstheticket.com.au" });
    }
    const registerResponse: RegisterResponse = await response.json();

    if (registerResponse.token) {
      setNormalToken(registerResponse.token);
    }

    return new RegisterApiResponse({ data: registerResponse });
  } catch (error: any) {
    console.error(error);
    return new RegisterApiResponse({ error: error.message });
  }
};

export const registerBasicInfo = async (registerData: RegisterBasicInfoInterface): Promise<any> => {
  try {
    const body: string = JSON.stringify(registerData);
    const res = await defaultFetch(`/api/backend/authentication/register-basic-info`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });
    if (res.status >= 400 && res.status < 600) {
      const resText = await res.json();
      return resText
    }
    return res;
  } catch (error) {
    return { error };
  }
};

export const resendConfirmationEmail = async (email: string): Promise<any> => {
  try {
    const res = await defaultFetch(`/api/backend/authentication/confirmation-email-resend/${email}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (res.status >= 400 && res.status < 600) {
      const resText = await res.json();
      return resText
    }
    return res;
  } catch (error) {
    return { error };
  }
};

export const confirmEmailAddress = async (confirmEmailAddressData: ConfirmEmailAddressInterface): Promise<ConfirmEmailResponse> => {
  try {
    const body: string = JSON.stringify(confirmEmailAddressData);
    const response = await defaultFetch(`/api/backend/authentication/confirm-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });

    if (response.status >= 400 && response.status < 600) {
      const problemDetails: ProblemDetails = await response.json();
      return new ConfirmEmailResponse({ error: extractErrorMessageFromProblemDetails(problemDetails) })
    }

    const confirmEmailData: ConfirmEmailData = await response.json();
    return new ConfirmEmailResponse({ data: confirmEmailData });
  } catch (error) {
    if (error instanceof Error) {
      return new ConfirmEmailResponse({ error: error.message });
    }

    return new ConfirmEmailResponse({ error: String(error) });
  }
};

export const sendConfirmPhoneNumberSMS = async (confirmPhoneNumber: SendConfirmPhoneNumberSMSInterface): Promise<any> => {
  try {
    const body: string = JSON.stringify(confirmPhoneNumber);
    const res = await defaultFetch(`/api/backend/authentication/send-phone-number-confirmation-sms`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });
    if (res.status >= 400 && res.status < 600) {
      const resText = await res.json();
      return resText
    }
    return res;
  } catch (error) {
    return { error };
  }
};

export const login = async (login: LoginInterface): Promise<LoginApiResponse> => {
  try {
    const body: string = JSON.stringify(login);
    const response = await defaultFetch(`/api/backend/authentication/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });

    if (response.status === 401 || response.status === 403) {
      return new LoginApiResponse({ error: "Account has been suspended. Please contact support at audience@thatstheticket.com.au" });
    }

    if (response.status === 400) {
      console.warn(await response.text());
      return new LoginApiResponse({ error: "Incorrect email or password. Please try again." });
    }

    if (response.status >= 500) {
      return new LoginApiResponse({ error: "Unable to log you in. Please contact support at audience@thatstheticket.com.au" });
    }

    const loginResponse: LoginResponse = await response.json();
    setNormalToken(loginResponse.token);
    setProfile(loginResponse.user);

    return new LoginApiResponse({ data: loginResponse });
  } catch (error: any) {
    console.error(error);
    return new LoginApiResponse({ error: error.message });
  }
};

export const magicLinkLogin = async (token: string): Promise<LoginApiResponse> => {
  try {
    const response = isLoggedIn() ? await fetchWithAuth(`/api/backend/authentication/magic-link-login/${token}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }) : await defaultFetch(`/api/backend/authentication/magic-link-login/${token}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 400) {
      const problemDetails: ProblemDetails = await response.json();
      return new LoginApiResponse({ error: problemDetails });
    }

    if (response.status >= 500) {
      return new LoginApiResponse({ error: "Unable to log you in. Please contact support at audience@thatstheticket.com.au" });
    }

    const loginResponse: LoginResponse = await response.json();
    setNormalToken(loginResponse.token);

    return new LoginApiResponse({ data: loginResponse });
  } catch (error: any) {
    console.error(error);
    return new LoginApiResponse({ error: error.message });
  }
};

export const sendMagicLink = async (email: string, expiredToken: string): Promise<any> => {
  try {
    const body: string = JSON.stringify({ email, expiredToken });
    const res = await defaultFetch(`/api/backend/authentication/send-magic-link`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body
    });
    if (res.status >= 400 && res.status < 600) {
      const resText = await res.text();
      console.warn(resText);
      throw new Error("Server not returned a valid response");
    }
  } catch (error) {
    return { error };
  }
};

export const forgotPasswordAdmin = async (
  login: LoginInterface
): Promise<any> => {
  try {
    const body: string = JSON.stringify(login);
    const res = await defaultFetch(`/api/backend/authentication/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });
    if (res.status >= 400 && res.status < 600) {
      const resText = await res.json();
      return resText
    }
    return res;
  } catch (error) {
    return { error };
  }
};

export const resetPasswordAdmin = async (
  resetPassword: ResetPasswordInterface
): Promise<any> => {
  try {
    const body: string = JSON.stringify(resetPassword);
    const res = await defaultFetch(`/api/backend/authentication/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });
    if (res.status >= 400 && res.status < 600) {
      const resText = await res.json();
      return resText
    }
    return res;
  } catch (error) {
    return { error };
  }
};

export const viewAsLogin = async (email: string): Promise<void> => {
  try {
    const body: string = JSON.stringify({ email: email });
    const response = await fetchWithAuth(`/api/backend/authentication/view-as-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });

    if (response.status >= 400 && response.status < 600) {
      const problemDetails = await response.text();
      console.error(problemDetails);
    }

    const loginResponse: LoginResponse = await response.json();

    if (loginResponse) {
      setViewAsToken(loginResponse.token);
      setViewAsProfile(loginResponse.user);
    }
  } catch (error) {
    console.error(error);
  }
};

export interface AllocationRights {
  canEditAllocations: number[];
  readOnlyAllocations: number[];
  canSendTicketsFromAllocation: number[];
}

export const refreshAllocationRights = async (): Promise<AllocationRights | null> => {
  try {
    const response = await fetchWithAuth(`/api/backend/authentication/refresh-allocation-rights`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status >= 400 && response.status < 600) {
      const problemDetails = await response.text();
      console.error(problemDetails);
      return null;
    }

    const allocationRights: AllocationRights = await response.json();

    // Update the user profile in local storage with the refreshed allocation rights
    updateUserAllocationRights(allocationRights);

    return allocationRights;
  } catch (error) {
    console.error(error);
    return null;
  }
};